import { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import useCookie from "../../hooks/useCookie";
import axios from "axios";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useContext } from "react";
import { EntityManagementContext } from "../../context/EntityManagementContext";

// Form to add teacher to database
export default function ManageTeacherForm() {
  const user = useCurrentUser();

  const {
    editingID,
    exit,
    mode,
    setMode,
    updateEntities
  } = useContext(EntityManagementContext);

  // Value of form elements; used to load data in edit mode
  // and grabbed upon save
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState(""); // Have users type password twice to confirm it
  
  // If the user has elected to edit their password
  const [editingPassword, setEditingPassword] = useState(false);

  // If a successful edit or create operation has just been performed
  const [success, setSuccess] = useState(false);

  // If the two passwords do not match
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // When set to true, displays an error badge that helps
  // user correct their error.
  const [errorState, setErrorState] = useState(false);

  // List of errors to display after creation/editing error
  // occurs; implemented as dictionary mapping input field
  // to associated list of error messages.
  const [errorDict, setErrorDict] = useState({});

  const csrftoken = useCookie("csrftoken");

  // Badge that appears upon sucessful creation/edit of teacher
  const successBadge = () => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 5000);
  };

  // If a class has been selected to edit, populate all
  // form options with currently saved data (except password)
  useEffect(() => {
    if (mode == "Edit") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      axios.get("/api/teachers/" + editingID)
        .then((response) => {
          let editingTeacher = response.data;
          setFirstName(editingTeacher.first_name);
          setLastName(editingTeacher.last_name);
          setEmail(editingTeacher.email);
          // No password should appear when user edits this field; 
          // they must set a new one.
          setPassword("");
        });
    } else if (mode === "Create") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      // Clear form
      clearForm();
    }
  }, [editingID]);

  // Clear all form values
  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setPassword2("");
  }

  // Cancel creation or editing of class
  const cancelAction = () => {
    if (mode == "Edit") {
      setMode("Create");
    }
    clearForm();
    exit();
  }

  // Organize validation error information for error badge display.
  //  errorObj: HTTP response carrying error information, as JSON object
  const handleFieldValidationErrors = (errorObj) => {
    // Gather errors, which will we use to update state at the end
    let currentErrorDict = {};

    // Get response body
    let errorBody = errorObj.response.data;

    // This is MVP - just handling errors from server-side validation
    // Would be better if we had client-side validation as well to make 
    // the user registration process faster

    // Handle first name related errors
    if (errorBody["first_name"] && errorBody["first_name"].length >= 1) {
      currentErrorDict["First name"] = errorBody["first_name"];
    }

    // Handle last name related errors
    if (errorBody["last_name"] && errorBody["last_name"].length >= 1) {
      currentErrorDict["Last name"] = errorBody["last_name"];
    }

    // Handle email (username) related errors
    if (errorBody["email"] && errorBody["email"].length >= 1) {
      currentErrorDict["Username"] = errorBody["email"];
    }

    setErrorDict(currentErrorDict);
    setErrorState(true);
  }

  // Submission of form
  // Depending on current mode, creates or saves entity to database
  const handleSubmit = async (e) => {
    // Prevent default behavior - do not clear inputs immediately
    e.preventDefault();

    // Clear error state
    setErrorState(false);
    setErrorDict({});

    const headers = {
      "X-CSRFToken": csrftoken,
    };

    // Confirm that passwords match before submitting
    if (password != password2) {
      setPasswordMismatch(true);
      return;
    } else {
      setPasswordMismatch(false);
    }

    // Use form values to create new entity in database
    if (mode == "Create") {
      // Create two galleries for the new teacher
      const gallery1 = await axios.post(
        "/api/galleries/",
        {
          name: "Recent Gallery",
        },
        { headers }
      );
      const gallery2 = await axios.post(
        "/api/galleries/",
        {
          name: "Saved Gallery",
        },
        { headers }
      );
      const newTeacher = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: password,
        is_staff: true,
        recent: gallery1.data.galleryID,
        saved: gallery2.data.galleryID,
      };
      const response = await axios
        .post("/api/teachers/", newTeacher, { headers })
        .then((response) => {
          successBadge();
          // Clear form values
          clearForm();
          // Update table with new teacher
          updateEntities(response.data);
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    } else if (mode == "Edit") { // Update existing entity in database
      const updatedTeacher = {
        first_name: firstName,
        last_name: lastName,
        email: email,
      };

      if (password != null && password != "") {
        updatedTeacher["password"] = password;
      }

      axios.patch("/api/teachers/" + editingID, updatedTeacher, { headers })
        .then((response) => {
          if (response.status == 200) {
            successBadge();
            // Clear form values
            clearForm();
            // Set mode back to create
            setMode("Create");
            // Update table with updated teacher
            updateEntities(response.data, editingPassword);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    }
  };

  const editPassword = () => {
    setEditingPassword(true);
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3" controlId="formInstructorFirstName">
        <Form.Label>First Name</Form.Label> {/* Form item: First name */}
        <Form.Control
          type="text"
          placeholder="Enter first name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formInstructorLastName">
        <Form.Label>Last Name</Form.Label> {/* Form item: Last name */}
        <Form.Control
          type="text"
          placeholder="Enter last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formInstructorEmail">
        <Form.Label>Username</Form.Label> {/* Form item: Email */}
        <Form.Control
          type="text"
          placeholder="Enter username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Form.Group>
      <Form.Group controlId="formInstructorPassword">
        <Form.Label>Password</Form.Label> {/* Form item: Password */}
        {editingPassword || mode == "Create" ? (
          <>
            {/* Password input */}
            <Form.Control
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {/* Confirm password input */}
            <Form.Control
              type="password"
              placeholder="Confirm password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
            />
            {passwordMismatch ? (
              <p id="password-mismatch">Passwords do not match.</p>
            ) : (<></>)}
          </>
        ) : (
          <button className="admin-button" onClick={editPassword}>
            Change Password
          </button>
        )}
        {user && user.id == editingID && editingPassword ? (
          <p id="self-edit-disclaimer">
            WARNING: You will be logged out when you change your password.
          </p>
        ) : (<></>)}
      </Form.Group>

      {/* Submit button */}
      <Button id="createTeacher" className="admin-button" type="submit">
        {mode} Teacher
      </Button>

      {/* Cancel button */}
      <Button
        className="admin-button"
        onClick={cancelAction}
        style={{ marginTop: "5px" }}
      >
        Cancel
      </Button>

      {/* Show success badge if action was successful */}
      {success ? (
        <div
          className="alert alert-success"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          Successful!
        </div>
      ) : null}

      {/* If there are any erroneous inputs, let the user know */}
      {errorState ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          <strong>An error occurred.</strong><br /><br />
          {Object.entries(errorDict).map(
            (field) =>
              <>
                <i>{field[0]}</i>
                <ul>
                  {field[1].map((error) =>
                    <li>{error}</li>
                  )}
                </ul>
              </>
          )}
        </div>
      ) : null}
    </Form>
  );
}
